import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  BasicModal,
  EditableTable,
  RadioButton,
  ToggleInputButton,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { editableColumn } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/EditableTable';
import {
  commasForNumbers,
  formatDate,
} from '../../../core-utils/helper-utils/helper';
import moment from 'moment';
import { TimeSeriesEntry } from '../../../pages/FinancialForensicsPage/types/EmployeeCount.types';

export interface BuildScenarioModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
   
  handleSubmit: (data: any) => void;
  breakdownType: 'Annually' | 'Monthly' | 'Quarterly';
  tableData: {
    Monthly: any[];
    Quarterly: any[];
    Annually: any[];
  };
  onTableDataChange: (
     
    type: 'Monthly' | 'Quarterly' | 'Annually',
     
    updatedData: any[],
  ) => void;
  serviceName?: string;
  startDate?: string;
  endDate?: string;
  scenarioFee?: number;
  capValues?: {
    [key: string]: number | null;
  };
  setCapValues?: React.Dispatch<
    React.SetStateAction<{
      [key: string]: number | null;
    }>
  >;
}

const useStyles = makeStyles()({
  flex: {
    display: 'flex',
    alignItems: 'center',
  },
  capFeeGrid: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    marginTop: '20px',
    height: '65px',
  },
});

const BuildScenarioModal: React.FC<BuildScenarioModalProps> = observer(
  ({
    open,
    setOpen,
    handleSubmit,
    onTableDataChange,
    breakdownType = 'Annually',
    serviceName,
    startDate,
    endDate,
    tableData,
    scenarioFee,
    capValues,
    setCapValues,
  }) => {
    const { classes } = useStyles();
    const [categoryTypes, setCategoryTypes] = useState([
      { name: 'Monthly', checked: breakdownType === 'Monthly' },
      { name: 'Quarterly', checked: breakdownType === 'Quarterly' },
      { name: 'Annually', checked: breakdownType === 'Annually' },
    ]);
    const [selectedType, setSelectedType] = useState<
      'Monthly' | 'Quarterly' | 'Annually'
    >(breakdownType);
    const [openIndex, setOpenIndex] = useState<null | number>(
      capValues?.capValue && capValues?.capValue !== null
        ? 0
        : capValues?.flatFee && capValues?.flatFee !== null
        ? 1
        : null,
    );

    const handleToggle = (index: number) => {
      setOpenIndex((prev) => (prev === index ? null : index)); // toggle open state
    };

    const handleValueChange = (field: string, val: number) => {
      setCapValues && setCapValues({ [field]: val });
    };

    const getColumns = (type: string): editableColumn[] => {
      let values: TimeSeriesEntry[] = [];
      const start = moment(startDate);
      const end = moment(endDate);

      if (type === 'Monthly') {
        const current = start.clone();
        while (current.isSameOrBefore(end, 'month')) {
          values.push({
            dateRange: formatDate(current.format(), "MMM'YY"),
            value: null,
          });
          current.add(1, 'month');
        }
      } else if (type === 'Quarterly') {
        const current = start.clone().startOf('quarter');
        while (current.isSameOrBefore(end, 'quarter')) {
          values.push({
            dateRange: formatDate(current.format(), "[Q]Q'YY"),
            value: null,
          });
          current.add(1, 'quarter');
        }
      } else if (type === 'Annually') {
        const startYear = start.format('YYYY');
        const endYear = end.format('YYYY');
        values = [
          {
            dateRange:
              startYear === endYear ? startYear : `${startYear} - ${endYear}`,
            value: null,
          },
        ];
      }

      const dateRangeColumns: editableColumn[] = values.map((value: any) => ({
        key: value.dateRange,
        label: value.dateRange,
        editable: true,
        type: 'number',
        prefix: value.prefix,
      }));

      const firstColumn: editableColumn = {
        key: 'contribution',
        label: 'Contribution',
        editable: false,
        width: '150px',
      };

      return [firstColumn, ...dateRangeColumns];
    };

    const [tableColumns, setTableColumns] = useState<editableColumn[]>(
      getColumns(breakdownType),
    );

    const handleRadioChange = (
      selected: 'Monthly' | 'Quarterly' | 'Annually',
    ) => {
      setCategoryTypes((prev) =>
        prev.map((type) => ({
          ...type,
          checked: type.name === selected,
        })),
      );
      setSelectedType(selected);
      setTableColumns(getColumns(selected));
    };

    const handleClose = () => {
      setOpen(false);
    };

    const handleConfirm = () => {
      handleSubmit(tableData[selectedType]);
    };

    useEffect(() => {
      setCategoryTypes((prev) =>
         
        prev.map((type) => ({
          ...type,
          checked: type.name === breakdownType,
        })),
      );
      setSelectedType(breakdownType);
      setTableColumns(getColumns(breakdownType));
    }, [breakdownType]);

    return (
      <BasicModal
        open={open}
        title={`Build Scenario - ${serviceName}`}
        width="730px"
        rootStyles={{
          padding: '20px',
        }}
        headerStyles={{ padding: '0px 0px 20px 0px !important' }}
        bodyStyles={{ padding: '32px 32px 0px 32px' }}
        body={
          <Grid>
            <Grid
              className={classes.flex}
              style={{ gap: '32px', marginBottom: '28px' }}
            >
              {categoryTypes.map((item, index) => (
                <Grid
                  className={classes.flex}
                  style={{ gap: '16px' }}
                  key={index}
                >
                  <RadioButton
                    color={patientAccessTheme.palette.primary[400]}
                    checked={item.checked}
                    handleChange={() => handleRadioChange(item.name as any)}
                  />
                  <Typography
                    textColor={patientAccessTheme.palette.text.highemphasis}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType="semiBold"
                  >
                    {item.name}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            <Grid>
              <EditableTable
                data={tableData[selectedType]}
                columns={tableColumns}
                onChange={(updated) => onTableDataChange(selectedType, updated)}
                bodyCellStyles={{
                  width: '100px',
                  padding: '0.5rem',
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
              <Grid className={classes.capFeeGrid}>
                <ToggleInputButton
                  label={'Add Total Cap'}
                  value={capValues?.['capValue']!}
                  open={openIndex === 0}
                  onToggle={() => handleToggle(0)}
                  onChange={(val) => handleValueChange('capValue', Number(val))}
                />
                <ToggleInputButton
                  label={'Override with Flat Fee'}
                  value={capValues?.['flatFee']!}
                  open={openIndex === 1}
                  onToggle={() => handleToggle(1)}
                  onChange={(val) => handleValueChange('flatFee', Number(val))}
                />
              </Grid>
              <Typography
                textColor={patientAccessTheme.palette.text.highemphasis}
                fontSize="12px"
                lineHeight="16px"
                fontType="regular"
                margin="20px 0 0 0"
              >
                Total Scenario Fee : $
                {commasForNumbers(scenarioFee?.toString() || '0')}
              </Typography>
            </Grid>
          </Grid>
        }
        cancelButtonLabel="Cancel"
        confirmButtonLabel="Submit"
        handleCancel={handleClose}
        handleConfirm={handleConfirm}
        handleClose={handleClose}
      />
    );
  },
);

export default BuildScenarioModal;
